import { Input } from '@/components/ui/input';
import type { ChartTypeConfigPanelProps } from './registry';
import type { ChangeEvent } from 'react';

/**
 * Extra controls for the xy-scatter geometry: optional quadrant divider values
 * (a vertical line on X, a horizontal line on Y) that split the plane into
 * clinical quadrants, plus optional semantic labels at the four axis extremes
 * (lento/rápido, impreciso/cuidadoso). Rendered by the modal via the
 * ConfigPanel seam.
 */
export default function XyScatterConfigPanel({
    disabled,
    quadrant,
    onQuadrantChange,
    axisEndLabels,
    onAxisEndLabelsChange,
}: ChartTypeConfigPanelProps) {
    return (
        <div className="space-y-4">
            <div className="space-y-2 rounded-xl border border-border bg-surface/40 p-3">
                <p className="text-sm font-medium text-foreground">
                    Quadrantes <span className="font-normal text-muted-foreground">(opcional)</span>
                </p>
                <p className="text-xs text-muted-foreground">
                    Divida o gráfico com uma linha vertical (X) e/ou horizontal (Y).
                </p>
                <div className="flex items-center gap-1.5">
                    <Input
                        value={quadrant.x}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onQuadrantChange({ x: event.target.value })
                        }
                        placeholder="Divisor X"
                        inputMode="decimal"
                        className="h-8 flex-1 text-xs"
                        disabled={disabled}
                    />
                    <Input
                        value={quadrant.y}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onQuadrantChange({ y: event.target.value })
                        }
                        placeholder="Divisor Y"
                        inputMode="decimal"
                        className="h-8 flex-1 text-xs"
                        disabled={disabled}
                    />
                </div>
            </div>

            <div className="space-y-2 rounded-xl border border-border bg-surface/40 p-3">
                <p className="text-sm font-medium text-foreground">
                    Rótulos das extremidades{' '}
                    <span className="font-normal text-muted-foreground">(opcional)</span>
                </p>
                <p className="text-xs text-muted-foreground">
                    Nomeie as pontas dos eixos (ex.: lento → rápido, impreciso → cuidadoso).
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                    <Input
                        value={axisEndLabels.x_min}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onAxisEndLabelsChange({ x_min: event.target.value })
                        }
                        placeholder="Esquerda (ex.: lento)"
                        className="h-8 text-xs"
                        disabled={disabled}
                    />
                    <Input
                        value={axisEndLabels.x_max}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onAxisEndLabelsChange({ x_max: event.target.value })
                        }
                        placeholder="Direita (ex.: rápido)"
                        className="h-8 text-xs"
                        disabled={disabled}
                    />
                    <Input
                        value={axisEndLabels.y_min}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onAxisEndLabelsChange({ y_min: event.target.value })
                        }
                        placeholder="Base (ex.: impreciso)"
                        className="h-8 text-xs"
                        disabled={disabled}
                    />
                    <Input
                        value={axisEndLabels.y_max}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onAxisEndLabelsChange({ y_max: event.target.value })
                        }
                        placeholder="Topo (ex.: cuidadoso)"
                        className="h-8 text-xs"
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    );
}
